import { isSafeUrl } from './htmlPolicy';

// Reading a menu into what a header can draw. The rows arrive from two places that spell an address
// differently — a store menu stores `url`, a list typed into the element stores `href` — and from a
// merchant, so every row is checked before a page points at it.

/** One link a shopper can click: its words, an address the page may point at, and what drops from it. */
export type MenuLink = { label: string; href: string | undefined; image: string | undefined; children: MenuLink[] };

/** How deep a header goes: a drop-down, and the columns of links its links head in a mega menu. */
const MAX_DEPTH = 3;

// Resolves a row's address, or nothing when it is empty or its scheme could run something.
function addressOf(row: Record<string, unknown>): string | undefined {
  const { href, url } = row;
  const raw = (typeof href === 'string' ? href : typeof url === 'string' ? url : '').trim();
  return raw !== '' && isSafeUrl(raw, false) ? raw : undefined;
}

// Reads one link and, unless it is already a drop-down's child, the links beneath it.
function menuLink(row: unknown, depth: number): MenuLink | null {
  if (typeof row !== 'object' || row === null) return null;
  const record = row as Record<string, unknown>;
  const label = typeof record.label === 'string' ? record.label.trim() : '';
  if (label === '') return null;
  const children = depth < MAX_DEPTH ? menuLinks(record.children, depth + 1) : [];
  const picture = typeof record.image === 'string' && isSafeUrl(record.image.trim(), false) ? record.image.trim() : undefined;
  return { label, href: addressOf(record), image: picture, children };
}

// Reads every link in order, dropping the rows that say nothing a shopper could click.
export function menuLinks(rows: unknown, depth = 1): MenuLink[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => menuLink(row, depth)).filter((link): link is MenuLink => link !== null);
}
